import React, { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/browser';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTenancy } from '../contexts/TenancyContext';
import { X, ImagePlus } from 'lucide-react';

type FeedbackDialogProps = {
  children: React.ReactNode;
};

export function FeedbackDialog({ children }: FeedbackDialogProps) {
  const { currentUser } = useTenancy();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState('');
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotName, setScreenshotName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get user data from local storage or default to empty strings
  useEffect(() => {
    if (!currentUser || !currentUser.firstName || !currentUser.lastName || !currentUser.email || currentUser.firstName === '' || currentUser.lastName === '' || currentUser.email === '') return;
    try {
      if (currentUser) {
        setName(currentUser.firstName + ' ' + currentUser.lastName);
        setEmail(currentUser.email);
      }
    } catch (error) {
      console.error('Error getting user info from localStorage:', error);
    }
  }, [currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      console.log('Submitting feedback:', feedback);
      // Send user feedback using Sentry's proper feedback API
      Sentry.captureFeedback(
        { message: feedback, name: currentUser?.firstName + ' ' + currentUser?.lastName || name, email: currentUser?.email || email },
        {
          captureContext: {
            tags: { feedbackType: "user_initiated" },
            user: {
              email: currentUser?.email || email,
              username: currentUser?.firstName + ' ' + currentUser?.lastName
            }
          },
          attachments: screenshot && screenshotName ? [
            {
              filename: screenshotName,
              data: screenshot.split(',')[1], // Remove the data:image/png;base64, part
            },
          ] : undefined
        }
      );

      console.log('Feedback submitted successfully');

      // Reset form and close dialog
      setFeedback('');
      setScreenshot(null);
      setScreenshotName(null);
      setOpen(false);
      
      toast.success("Thank you for your feedback! We appreciate your input.");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("There was a problem submitting your feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] bg-white dark:bg-secondary">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
          <DialogDescription>
            We value your feedback! Let us know what you think about the application or report any issues you've encountered.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input 
              id="name" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              placeholder="Your name"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email" 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              placeholder="your.email@example.com"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="feedback" className="text-right">
              Your Feedback
            </Label>
            <Textarea 
              id="feedback" 
              value={feedback} 
              onChange={(e) => setFeedback(e.target.value)} 
              placeholder="What's on your mind?"
              className="col-span-3"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="screenshot" className="text-right">
              Screenshot
            </Label>
            <div className="col-span-3">
              {screenshot ? (
                <div className="relative w-full h-32 bg-muted rounded-md overflow-hidden mb-2">
                  <img 
                    src={screenshot} 
                    alt="Screenshot Preview" 
                    className="h-full w-auto mx-auto object-contain" 
                  />
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="icon" 
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/80 p-0"
                    onClick={() => {
                      setScreenshot(null);
                      setScreenshotName(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full">
                  <label
                    htmlFor="screenshot-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/50 hover:bg-muted"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <ImagePlus className="w-8 h-8 mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Click to upload a screenshot</p>
                    </div>
                    <input
                      id="screenshot-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      ref={fileInputRef}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            if (event.target?.result) {
                              setScreenshot(event.target.result as string);
                              setScreenshotName(file.name);
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? 'Sending...' : 'Send Feedback'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
